import { useEffect, useRef } from 'react';
import type { BrandAsset } from '../../services/editingApi';
import type { EditList } from '../../utils/editing/editList';
import { outputDurationMs } from '../../utils/editing/editList';

/**
 * Hears the mix while previewing: music under the video (looped, at its gain,
 * lowered while a voiceover plays) and voiceovers at their times, through plain
 * <audio> elements kept in step with the preview's output clock. The original
 * sound's gain is applied to the <video> itself. An approximation of the
 * render's ducking, close enough to judge levels; the export is the truth.
 */

const dbToGain = (db: number): number => Math.min(1, Math.pow(10, db / 20));
/** Re-sync an element when it drifts more than this from the clock. */
const DRIFT_MS = 250;

interface Options {
  list: EditList;
  assets: BrandAsset[];
  outputMs: number;
  playing: boolean;
  video: HTMLVideoElement | null;
  muted: boolean;
}

export function usePreviewAudio({ list, assets, outputMs, playing, video, muted }: Options): void {
  const musicRef = useRef<HTMLAudioElement | null>(null);
  const musicUrlRef = useRef<string | null>(null);
  const voicesRef = useRef<Record<string, HTMLAudioElement>>({});

  // One element per sound, created when the list first names it.
  useEffect(() => {
    const musicAsset = list.music ? assets.find((asset) => asset.id === list.music?.assetId) : null;
    const url = musicAsset?.url || null;
    if (url !== musicUrlRef.current) {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.src = '';
      }
      musicRef.current = url ? new Audio(url) : null;
      musicUrlRef.current = url;
      if (musicRef.current) musicRef.current.preload = 'auto';
    }
    const wanted: Record<string, string> = {};
    list.voiceovers.forEach((voiceover) => {
      const asset = voiceover.source.assetId ? assets.find((candidate) => candidate.id === voiceover.source.assetId) : null;
      if (asset) wanted[voiceover.id] = asset.url;
    });
    const current = voicesRef.current;
    Object.keys(current).forEach((id) => {
      if (wanted[id] !== current[id].src && wanted[id] !== current[id].getAttribute('data-src')) {
        current[id].pause();
        delete current[id];
      }
    });
    Object.keys(wanted).forEach((id) => {
      if (!current[id]) {
        const el = new Audio(wanted[id]);
        el.preload = 'auto';
        el.setAttribute('data-src', wanted[id]);
        current[id] = el;
      }
    });
  }, [assets, list.music, list.voiceovers]);

  // Keep everything in step with the clock.
  useEffect(() => {
    const total = outputDurationMs(list);
    let voiceSpeaking = false;
    list.voiceovers.forEach((voiceover) => {
      const el = voicesRef.current[voiceover.id];
      if (!el) return;
      const local = outputMs - voiceover.atMs;
      const lengthMs = Number.isFinite(el.duration) ? el.duration * 1000 : Infinity;
      const active = playing && !muted && local >= 0 && local < lengthMs;
      el.volume = dbToGain(voiceover.gainDb);
      if (active) {
        voiceSpeaking = voiceSpeaking || voiceover.duckOriginal;
        if (Math.abs(el.currentTime * 1000 - local) > DRIFT_MS) el.currentTime = local / 1000;
        if (el.paused) el.play().catch(() => undefined);
      } else if (!el.paused) {
        el.pause();
      }
    });
    if (video) {
      const base = dbToGain(list.audio.originalGainDb);
      video.volume = Math.max(0, Math.min(1, voiceSpeaking ? base * 0.3 : base));
    }
    const music = musicRef.current;
    if (music && list.music) {
      const local = outputMs - list.music.startMs;
      const active = playing && !muted && local >= 0 && outputMs < total;
      music.loop = list.music.loop;
      const ducked = list.music.ducking ? dbToGain(list.music.gainDb - list.music.duckDb * 0.7) : dbToGain(list.music.gainDb);
      music.volume = ducked;
      if (active) {
        const lengthMs = Number.isFinite(music.duration) && music.duration > 0 ? music.duration * 1000 : null;
        const want = lengthMs && list.music.loop ? local % lengthMs : local;
        if (lengthMs && !list.music.loop && local >= lengthMs) {
          if (!music.paused) music.pause();
        } else {
          if (Math.abs(music.currentTime * 1000 - want) > DRIFT_MS) music.currentTime = want / 1000;
          if (music.paused) music.play().catch(() => undefined);
        }
      } else if (!music.paused) {
        music.pause();
      }
    }
  }, [list, muted, outputMs, playing, video]);

  // Silence everything when the studio closes.
  useEffect(() => () => {
    if (musicRef.current) musicRef.current.pause();
    Object.keys(voicesRef.current).forEach((id) => voicesRef.current[id].pause());
  }, []);
}
